import { useEffect, useState, type FocusEvent, type ChangeEvent } from "react";
import type { IChechboxBase, IChechboxBaseInternalProps } from "libs/design-system/src/types/checkbox/checkbox_base";
import styles from "./checkbox-base.module.scss";

export const CheckboxBase = ({
  id,
  size = "sm",
  icon = "tick",
  type = "checkbox",
  selected = false,
  checked = false,
  disabled = false,
  onChangeHandler,
  ...internalProps
}: IChechboxBase) => {

  const { hovered = false, focused = false } = internalProps as IChechboxBaseInternalProps;

  const [isHovered, setIsHovered] = useState(hovered);
  const [isFocused, setIsFocused] = useState(focused);
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => {
    setIsHovered(hovered);
  }, [hovered]);

  useEffect(() => {
    setIsFocused(focused);
  }, [focused]);

  useEffect(() => {
    setIsChecked(checked);
    onChangeHandler?.(checked);
  }, [checked]);

  const onMouseOverHandler = () => {
    setIsHovered(true);
  };

  const onMouseOutHandler = () => {
    setIsHovered(false);
  };

  const onFocusHandler = (e: FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsFocused(true);
  };

  const onBlurHandler = (e: FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsFocused(false);
  };

  const handleInputChange = (event: ChangeEvent) => {
    event.stopPropagation();

    setIsChecked((prev) => {
      const newCheckedState = !prev;
      onChangeHandler?.(newCheckedState);

      return newCheckedState;
    });
  };

  const isRadio = type === "radio";
  const isSelected = isRadio ? selected : isChecked;

  return (
    <div
      className={styles["checkbox-base"]}
      data-checkbox-base-size={size}
      data-checkbox-base-icon={icon}
      data-checkbox-base-type={type}
      onMouseOver={onMouseOverHandler}
      onMouseOut={onMouseOutHandler}
      onFocusCapture={onFocusHandler}
      onBlurCapture={onBlurHandler}
      tabIndex={0}
    >
      <input
        id={id}
        type={isRadio ? "radio" : "checkbox"}
        className={styles["checkbox-base__input"]}
        checked={isSelected}
        disabled={disabled}
        onChange={handleInputChange}
        aria-label={isRadio ? "Radio" : "Checkbox"}
      />
      <label
        htmlFor={id}
        className={styles["checkbox-base__checkbox"]}
        data-checkbox-base-hovered={isHovered}
        data-checkbox-base-focused={isFocused}
        data-checkbox-base-selected={isSelected}
        tabIndex={0}
      ></label>
    </div>
  )
}
